import { z } from "zod";

import { NewsletterBlockType, NewsletterContentMode, NewsletterReleaseStatus, NewsletterSubscriberStatus } from "./newsletter";

export const newsletterSubscriberStatusSchema = z.nativeEnum(NewsletterSubscriberStatus);
export type NewsletterSubscriberStatusValue = z.infer<typeof newsletterSubscriberStatusSchema>;

export const newsletterReleaseStatusSchema = z.nativeEnum(NewsletterReleaseStatus);
export type NewsletterReleaseStatusValue = z.infer<typeof newsletterReleaseStatusSchema>;

export const newsletterContentModeSchema = z.nativeEnum(NewsletterContentMode);
export type NewsletterContentModeValue = z.infer<typeof newsletterContentModeSchema>;

/**
 * Custom HTML for releases authored outside the block editor. Size-capped, never rendered into
 * the admin app's DOM (only inside sandboxed iframes and the outgoing email).
 */
export const newsletterHtmlSchema = z.string().max(400_000);

/* -------------------------------------------------------------------------------------------------
 * Email document (block model)
 *
 * A release's body is a structured document, not raw HTML. The admin studio edits blocks, the
 * backend renders them into table-based, inline-styled email HTML. Keeping the model here means
 * the editor, the API validation, and the renderer all agree on one shape.
 * -----------------------------------------------------------------------------------------------*/

const blockAlignSchema = z.enum(["left", "center", "right"]);
export type NewsletterBlockAlign = z.infer<typeof blockAlignSchema>;

const hexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/);
/** Optional per-block color override; absent means the theme decides. */
const colorOverrideSchema = hexColorSchema.optional();

/**
 * Email-safe font families. Each value maps to a full fallback stack in the renderer, so what
 * the admin picks is what most inboxes can actually show.
 */
export const newsletterFontFamilySchema = z.enum([
  "sans",
  "arial",
  "helvetica",
  "verdana",
  "tahoma",
  "trebuchet",
  "segoe",
  "calibri",
  "lucida",
  "serif",
  "times",
  "garamond",
  "palatino",
  "cambria",
  "mono",
  "consolas",
  "comic",
]);
export type NewsletterFontFamily = z.infer<typeof newsletterFontFamilySchema>;

/** Optional per-block font override; absent means the theme decides. */
const fontOverrideSchema = newsletterFontFamilySchema.optional();

/** Editor-assigned block identity: stable across edits so reorder and selection can track blocks. */
const blockIdSchema = z.string().min(1).max(64);

/**
 * Text-carrying blocks accept a small inline markup subset: `**bold**`, `*italic*`, and
 * `[label](https://url)` links. The renderer escapes everything else, so the document can never
 * smuggle raw HTML into an email.
 */
const inlineTextSchema = z.string().max(5000);

export const newsletterHeadingBlockSchema = z.object({
  id: blockIdSchema,
  type: z.literal(NewsletterBlockType.HEADING),
  text: z.string().max(300),
  level: z.union([z.literal(1), z.literal(2)]).default(1),
  align: blockAlignSchema.default("left"),
  color: colorOverrideSchema,
  font: fontOverrideSchema,
});
export type NewsletterHeadingBlock = z.infer<typeof newsletterHeadingBlockSchema>;

export const newsletterTextBlockSchema = z.object({
  id: blockIdSchema,
  type: z.literal(NewsletterBlockType.TEXT),
  text: inlineTextSchema,
  align: blockAlignSchema.default("left"),
  size: z.enum(["sm", "md", "lg"]).default("md"),
  color: colorOverrideSchema,
  font: fontOverrideSchema,
});
export type NewsletterTextBlock = z.infer<typeof newsletterTextBlockSchema>;

export const newsletterListBlockSchema = z.object({
  id: blockIdSchema,
  type: z.literal(NewsletterBlockType.LIST),
  color: colorOverrideSchema,
  font: fontOverrideSchema,
  items: z.array(z.string().max(500)).max(30).default([]),
  style: z.enum(["bullet", "number"]).default("bullet"),
});
export type NewsletterListBlock = z.infer<typeof newsletterListBlockSchema>;

/**
 * How a pull quote is dressed. No rules, bars or boxes: a quote earns its place with size, weight
 * and whitespace, which is also all a mail client renders identically everywhere.
 */
export const newsletterQuoteStyleSchema = z.enum([
  "editorial",
  "serif",
  "mark",
  "panel",
  "testimonial",
  "inset",
]);
export type NewsletterQuoteStyle = z.infer<typeof newsletterQuoteStyleSchema>;

export const newsletterQuoteBlockSchema = z.object({
  id: blockIdSchema,
  type: z.literal(NewsletterBlockType.QUOTE),
  text: inlineTextSchema,
  attribution: z.string().max(200).default(""),
  style: newsletterQuoteStyleSchema.default("editorial"),
  /** Chrome color (bar, panel tint, quote mark); absent means the theme accent. */
  color: colorOverrideSchema,
  font: fontOverrideSchema,
});
export type NewsletterQuoteBlock = z.infer<typeof newsletterQuoteBlockSchema>;

export const newsletterButtonVariantSchema = z.enum(["filled", "outline", "soft", "link"]);
export type NewsletterButtonVariant = z.infer<typeof newsletterButtonVariantSchema>;

/** One call to action. A button block holds up to three of them, side by side in one row. */
export const newsletterButtonItemSchema = z.object({
  label: z.string().max(80),
  url: z.string().max(2000),
  variant: newsletterButtonVariantSchema.default("filled"),
  /** Custom button color; absent means the theme accent. */
  color: colorOverrideSchema,
});
export type NewsletterButtonItem = z.infer<typeof newsletterButtonItemSchema>;

export const newsletterButtonBlockSchema = z.object({
  id: blockIdSchema,
  type: z.literal(NewsletterBlockType.BUTTON),
  buttons: z.array(newsletterButtonItemSchema).min(1).max(3),
  align: blockAlignSchema.default("center"),
  size: z.enum(["sm", "md", "lg"]).default("md"),
  /** Stretch the row edge to edge. Ignored when the row holds more than one button. */
  fullWidth: z.boolean().default(false),
  font: fontOverrideSchema,
});
export type NewsletterButtonBlock = z.infer<typeof newsletterButtonBlockSchema>;

export const newsletterImageBlockSchema = z.object({
  id: blockIdSchema,
  type: z.literal(NewsletterBlockType.IMAGE),
  url: z.string().max(2000),
  alt: z.string().max(300).default(""),
  href: z.string().max(2000).default(""),
  width: z.enum(["full", "half", "third"]).default("full"),
  align: blockAlignSchema.default("center"),
  rounded: z.boolean().default(true),
});
export type NewsletterImageBlock = z.infer<typeof newsletterImageBlockSchema>;

export const newsletterDividerBlockSchema = z.object({
  id: blockIdSchema,
  type: z.literal(NewsletterBlockType.DIVIDER),
  style: z.enum(["solid", "dashed", "dotted"]).default("solid"),
  width: z.enum(["full", "short"]).default("full"),
  color: colorOverrideSchema,
});
export type NewsletterDividerBlock = z.infer<typeof newsletterDividerBlockSchema>;

/**
 * What sits in the gap. "blank" is pure whitespace; the ornaments are centered character rows,
 * which every mail client can draw, except "fade", a gradient hairline that degrades to blank in
 * Outlook's Word engine. A spacer losing its ornament is still a spacer, so that is safe.
 */
export const newsletterSpacerPatternSchema = z.enum([
  "blank",
  "dots",
  "dashes",
  "stars",
  "diamonds",
  "wave",
  "fade",
]);
export type NewsletterSpacerPattern = z.infer<typeof newsletterSpacerPatternSchema>;

export const newsletterSpacerBlockSchema = z.object({
  id: blockIdSchema,
  type: z.literal(NewsletterBlockType.SPACER),
  size: z.enum(["sm", "md", "lg", "xl"]).default("md"),
  pattern: newsletterSpacerPatternSchema.default("blank"),
  /** Ornament color; absent means the theme's hairline grey. */
  color: colorOverrideSchema,
});
export type NewsletterSpacerBlock = z.infer<typeof newsletterSpacerBlockSchema>;

const newsletterBlockUnion = z.discriminatedUnion("type", [
  newsletterHeadingBlockSchema,
  newsletterTextBlockSchema,
  newsletterListBlockSchema,
  newsletterQuoteBlockSchema,
  newsletterButtonBlockSchema,
  newsletterImageBlockSchema,
  newsletterDividerBlockSchema,
  newsletterSpacerBlockSchema,
]);

/**
 * Button blocks used to hold a single `label` / `url` / `variant` before a row could carry three.
 * Every read goes through this schema, so releases and templates saved under the old shape are
 * lifted into the new one here rather than in a data migration nobody can run twice.
 */
/** Quote styles that shipped before the rules came out, and what replaced each of them. */
const RETIRED_QUOTE_STYLES: Record<string, string> = {
  bar: "editorial",
  card: "panel",
  centered: "inset",
  boxed: "panel",
  plain: "editorial",
};

const liftLegacyBlock = (value: unknown): unknown => {
  if (typeof value !== "object" || value === null) return value;
  const block = value as Record<string, unknown>;

  if (block.type === NewsletterBlockType.QUOTE && typeof block.style === "string") {
    const replacement = RETIRED_QUOTE_STYLES[block.style];
    if (replacement) return { ...block, style: replacement };
    return value;
  }

  if (block.type !== NewsletterBlockType.BUTTON) return value;

  // An icon is just an emoji in the label, so a button that carried one as its own field keeps it
  // by having it folded back into the words.
  if (Array.isArray(block.buttons)) {
    if (!block.buttons.some((button) => typeof button === "object" && button !== null && "icon" in button)) {
      return value;
    }
    return {
      ...block,
      buttons: block.buttons.map((button) => {
        if (typeof button !== "object" || button === null) return button;
        const { icon, iconPosition, label, ...item } = button as Record<string, unknown>;
        if (typeof icon !== "string" || !icon) return { ...item, label };
        const text = typeof label === "string" ? label : "";
        return { ...item, label: iconPosition === "trailing" ? `${text} ${icon}` : `${icon} ${text}` };
      }),
    };
  }

  const { label, url, variant, color, ...rest } = block;
  return {
    ...rest,
    buttons: [{ label: label ?? "", url: url ?? "", variant: variant ?? "filled", color }],
  };
};

export const newsletterBlockSchema = z.preprocess(liftLegacyBlock, newsletterBlockUnion);
export type NewsletterBlock = z.infer<typeof newsletterBlockSchema>;

/**
 * Deliberate email-data colors, not brand tokens: an email renders in mail clients where the app's
 * CSS variables do not exist, so the accent is baked into the document as a hex value.
 */
export const newsletterThemeSchema = z.object({
  accentColor: hexColorSchema.default("#0f172a"),
  /** Page backdrop behind the email card. */
  backgroundColor: hexColorSchema.default("#f6f9fc"),
  /** The email card itself. */
  cardColor: hexColorSchema.default("#ffffff"),
  /** Default body text color; blocks can override per instance. */
  textColor: hexColorSchema.default("#374151"),
  fontFamily: newsletterFontFamilySchema.default("sans"),
  /** Corner rounding scale for the card, buttons, and images. */
  radius: z.enum(["none", "subtle", "soft", "round", "extra"]).default("soft"),
});
export type NewsletterTheme = z.infer<typeof newsletterThemeSchema>;

/** A fully-defaulted theme, for seeding new documents without repeating the values. */
export const defaultNewsletterTheme: NewsletterTheme = {
  accentColor: "#0f172a",
  backgroundColor: "#f6f9fc",
  cardColor: "#ffffff",
  textColor: "#374151",
  fontFamily: "sans",
  radius: "soft",
};

export const newsletterDocumentSchema = z.object({
  blocks: z.array(newsletterBlockSchema).max(100).default([]),
  theme: newsletterThemeSchema.default(defaultNewsletterTheme),
});
export type NewsletterDocument = z.infer<typeof newsletterDocumentSchema>;

/* -------------------------------------------------------------------------------------------------
 * Public API (subscribe / unsubscribe)
 * -----------------------------------------------------------------------------------------------*/

export const newsletterSubscribeSchema = z.object({
  email: z.string().email().max(320),
  /** Where the signup came from (e.g. "footer", "blog"). Free-form, shown in the admin list. */
  source: z.string().max(64).optional(),
});
export type NewsletterSubscribeInput = z.infer<typeof newsletterSubscribeSchema>;

export const newsletterTokenSchema = z.object({
  token: z.string().uuid(),
});
export type NewsletterTokenInput = z.infer<typeof newsletterTokenSchema>;

/* -------------------------------------------------------------------------------------------------
 * Admin API
 * -----------------------------------------------------------------------------------------------*/

export const newsletterReleaseRecordSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  subject: z.string(),
  preheader: z.string(),
  contentMode: newsletterContentModeSchema,
  document: newsletterDocumentSchema,
  html: newsletterHtmlSchema,
  status: newsletterReleaseStatusSchema,
  scheduledAt: z.string().nullable(),
  sentAt: z.string().nullable(),
  recipientCount: z.number().int().min(0),
  sentCount: z.number().int().min(0),
  failedCount: z.number().int().min(0),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type NewsletterReleaseRecord = z.infer<typeof newsletterReleaseRecordSchema>;

export const newsletterSubscriberRecordSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  status: newsletterSubscriberStatusSchema,
  source: z.string().nullable(),
  subscribedAt: z.string(),
  unsubscribedAt: z.string().nullable(),
});
export type NewsletterSubscriberRecord = z.infer<typeof newsletterSubscriberRecordSchema>;

export const newsletterTemplateRecordSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  document: newsletterDocumentSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type NewsletterTemplateRecord = z.infer<typeof newsletterTemplateRecordSchema>;

export const createNewsletterReleaseSchema = z.object({
  title: z.string().min(1).max(160),
  subject: z.string().max(200).default(""),
  preheader: z.string().max(200).default(""),
  document: newsletterDocumentSchema.default({ blocks: [], theme: defaultNewsletterTheme }),
});
export type CreateNewsletterReleaseInput = z.infer<typeof createNewsletterReleaseSchema>;

export const updateNewsletterReleaseSchema = z.object({
  title: z.string().min(1).max(160).optional(),
  subject: z.string().max(200).optional(),
  preheader: z.string().max(200).optional(),
  contentMode: newsletterContentModeSchema.optional(),
  document: newsletterDocumentSchema.optional(),
  html: newsletterHtmlSchema.optional(),
});
export type UpdateNewsletterReleaseInput = z.infer<typeof updateNewsletterReleaseSchema>;

export const scheduleNewsletterReleaseSchema = z.object({
  /** ISO timestamp. Must be in the future; the API rejects past dates. */
  scheduledAt: z.string().datetime(),
});
export type ScheduleNewsletterReleaseInput = z.infer<typeof scheduleNewsletterReleaseSchema>;

export const sendNewsletterTestSchema = z.object({
  email: z.string().email().max(320),
});
export type SendNewsletterTestInput = z.infer<typeof sendNewsletterTestSchema>;

export const listNewsletterReleasesParamsSchema = z.object({
  limit: z.coerce.number().int().positive().max(100).optional(),
  after: z.string().optional(),
  status: z.enum(["all", "draft", "scheduled", "sent"]).default("all"),
});
export type ListNewsletterReleasesParams = z.infer<typeof listNewsletterReleasesParamsSchema>;

export const listNewsletterSubscribersParamsSchema = z.object({
  limit: z.coerce.number().int().positive().max(100).optional(),
  after: z.string().optional(),
  status: z.enum(["all", "subscribed", "unsubscribed", "bounced", "complained"]).default("all"),
  search: z.string().max(200).optional(),
});
export type ListNewsletterSubscribersParams = z.infer<typeof listNewsletterSubscribersParamsSchema>;

export const saveNewsletterTemplateSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(300).default(""),
  document: newsletterDocumentSchema,
});
export type SaveNewsletterTemplateInput = z.infer<typeof saveNewsletterTemplateSchema>;

/* -------------------------------------------------------------------------------------------------
 * Public archive (hosted newsletter page)
 * -----------------------------------------------------------------------------------------------*/

/** A sent release as the public archive sees it: identity and metadata, no document body. */
export const publicNewsletterReleaseSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  subject: z.string(),
  preheader: z.string(),
  sentAt: z.string(),
});
export type PublicNewsletterRelease = z.infer<typeof publicNewsletterReleaseSchema>;

/**
 * The public release detail. Block releases render as a web article from `document`; custom-HTML
 * releases carry their `html` for a sandboxed iframe rendering.
 */
export const publicNewsletterReleaseDetailSchema = publicNewsletterReleaseSchema.extend({
  contentMode: newsletterContentModeSchema,
  document: newsletterDocumentSchema,
  html: newsletterHtmlSchema,
});
export type PublicNewsletterReleaseDetail = z.infer<typeof publicNewsletterReleaseDetailSchema>;

export const listPublicNewsletterReleasesParamsSchema = z.object({
  limit: z.coerce.number().int().positive().max(50).optional(),
  after: z.string().optional(),
});
export type ListPublicNewsletterReleasesParams = z.infer<typeof listPublicNewsletterReleasesParamsSchema>;

/* -------------------------------------------------------------------------------------------------
 * Analytics
 * -----------------------------------------------------------------------------------------------*/

export const newsletterSignupPointSchema = z.object({
  date: z.string(),
  count: z.number().int().min(0),
});
export type NewsletterSignupPoint = z.infer<typeof newsletterSignupPointSchema>;

/** The signed-in user's own subscription state, for one-tap subscribe on mobile and in-app. */
export const newsletterSubscriptionStatusSchema = z.object({
  subscribed: z.boolean(),
});
export type NewsletterSubscriptionStatus = z.infer<typeof newsletterSubscriptionStatusSchema>;

/** Engagement rollup for one release, as shown in the analytics performance table. */
export const newsletterReleasePerformanceSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  subject: z.string(),
  sentAt: z.string().nullable(),
  delivered: z.number().int().min(0),
  uniqueOpens: z.number().int().min(0),
  openRate: z.number().min(0),
  uniqueClicks: z.number().int().min(0),
  clickRate: z.number().min(0),
});
export type NewsletterReleasePerformance = z.infer<typeof newsletterReleasePerformanceSchema>;

export const newsletterSourcePointSchema = z.object({
  source: z.string(),
  count: z.number().int().min(0),
});
export type NewsletterSourcePoint = z.infer<typeof newsletterSourcePointSchema>;

/** Range selector for the analytics overview; everything range-scoped derives from `days`. */
export const newsletterAnalyticsParamsSchema = z.object({
  days: z.coerce.number().int().min(7).max(365).default(30),
});
export type NewsletterAnalyticsParams = z.infer<typeof newsletterAnalyticsParamsSchema>;

/** The analytics overview page payload: audience, engagement averages, and per-release rollups. */
export const newsletterAnalyticsOverviewSchema = z.object({
  /** The trailing window (days) the range-scoped numbers below cover. */
  rangeDays: z.number().int().min(1),
  activeSubscribers: z.number().int().min(0),
  unsubscribedSubscribers: z.number().int().min(0),
  /** Opt-outs within the selected range. */
  unsubscribedInRange: z.number().int().min(0),
  sentReleases: z.number().int().min(0),
  scheduledReleases: z.number().int().min(0),
  /** Confirmed signups within the selected range. */
  recentSignups: z.number().int().min(0),
  /** Averages over releases sent within the selected range. */
  averageOpenRate: z.number().min(0),
  averageClickRate: z.number().min(0),
  signups: z.array(newsletterSignupPointSchema),
  /** Acquisition breakdown for signups within the selected range. */
  sources: z.array(newsletterSourcePointSchema),
  /** Engagement rollups for recent sent releases; deliberately NOT range-scoped (the releases lane reads it). */
  releases: z.array(newsletterReleasePerformanceSchema),
});
export type NewsletterAnalyticsOverview = z.infer<typeof newsletterAnalyticsOverviewSchema>;

export const newsletterLinkStatsSchema = z.object({
  url: z.string(),
  totalClicks: z.number().int().min(0),
  uniqueClicks: z.number().int().min(0),
});
export type NewsletterLinkStats = z.infer<typeof newsletterLinkStatsSchema>;

export const newsletterEngagementPointSchema = z.object({
  date: z.string(),
  opens: z.number().int().min(0),
  clicks: z.number().int().min(0),
});
export type NewsletterEngagementPoint = z.infer<typeof newsletterEngagementPointSchema>;

/** Per-release drill-down: the numbers a sponsor asks for. */
export const newsletterReleaseAnalyticsSchema = z.object({
  release: newsletterReleaseRecordSchema,
  delivered: z.number().int().min(0),
  failed: z.number().int().min(0),
  totalOpens: z.number().int().min(0),
  uniqueOpens: z.number().int().min(0),
  openRate: z.number().min(0),
  totalClicks: z.number().int().min(0),
  uniqueClicks: z.number().int().min(0),
  clickRate: z.number().min(0),
  /** Clicks per destination URL, most clicked first. */
  links: z.array(newsletterLinkStatsSchema),
  /** Daily opens and clicks since the send, oldest first. */
  engagement: z.array(newsletterEngagementPointSchema),
});
export type NewsletterReleaseAnalytics = z.infer<typeof newsletterReleaseAnalyticsSchema>;

export const newsletterStatsSchema = z.object({
  activeSubscribers: z.number().int().min(0),
  unsubscribedSubscribers: z.number().int().min(0),
  sentReleases: z.number().int().min(0),
  scheduledReleases: z.number().int().min(0),
  lastSentAt: z.string().nullable(),
  /** Daily confirmed signups over the trailing 30 days, oldest first. */
  signups: z.array(newsletterSignupPointSchema),
});
export type NewsletterStats = z.infer<typeof newsletterStatsSchema>;
